import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'

import Panel from '../ui/Panel'
import Bar from '../ui/Bar'
import Btn from '../ui/Btn'
import './user.css'

/*
 * SKILL RATINGS — the assessment.
 *
 * GET manager/dashboard/userstats/{uuid} -> results: [{ StatName, Value }]
 * Five ratings on a 0–100 scale.
 *
 * This used to be five recharts donuts, one colour each, with the value printed
 * around the ring. Five numbers on a shared scale is a comparison, and a
 * comparison wants one axis: the Bar primitive puts every rating on the same
 * track so the strong and weak ends of a player are visible at a glance, with
 * the exact figure in tabular type on the right. A rank-coloured chart said
 * nothing the position of the bar does not already say, so the colour is one
 * (--mark) and the charting library is gone.
 */

export default function UserStats(params) {
  const [stats, setStats] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  const aliveRef = useRef(true)

  useEffect(() => {
    aliveRef.current = true
    loadData()
    return () => { aliveRef.current = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.uuid])

  const loadData = () => {
    setIsLoading(true)
    setFailed(false)

    axios.get(process.env.REACT_APP_API_SERVER + 'manager/dashboard/userstats/' + params.uuid, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("mngapiToken")
      }
    })
      .then(res => {
        const rows = (res.data && Array.isArray(res.data.results)) ? res.data.results : []

        // BarLabel is kept on the record: the old chart tooltip read it and other
        // callers of this shape may still expect it.
        rows.forEach(item => {
          item.BarLabel = item.StatName + ': ' + item.Value + '%'
        })

        if (!aliveRef.current) return
        setStats(rows)
        setIsLoading(false)
      })
      .catch(err => {
        if (!aliveRef.current) return
        setIsLoading(false)
        setFailed(true)
      })
  }

  const ratings = stats
    .map(item => ({ name: item.StatName, value: Number(item.Value) }))
    .filter(item => item.name && Number.isFinite(item.value))

  const average = ratings.length > 0
    ? Math.round(ratings.reduce((sum, item) => sum + item.value, 0) / ratings.length)
    : null

  let best = null
  let worst = null
  ratings.forEach(item => {
    if (!best || item.value > best.value) best = item
    if (!worst || item.value < worst.value) worst = item
  })

  return (
    <Panel
      title="Skill Ratings"
      subtitle={ratings.length > 0
        ? ratings.length + ' ratings on a 0–100 scale'
        : 'Ratings on a 0–100 scale'}
      actions={average !== null
        ? (
          <span className="usr-rating-note">
            <span className="mono">Avg</span>
            <b className="num">{average}</b>
          </span>
        )
        : null}
    >

      {isLoading && (
        <div className="usr-skel">
          <div className="skeleton usr-skel-line"></div>
          <div className="skeleton usr-skel-line"></div>
          <div className="skeleton usr-skel-line usr-skel-short"></div>
        </div>
      )}

      {!isLoading && failed && (
        <div className="usr-alert" role="alert">
          <b>The skill ratings could not be loaded.</b>
          <span>The server did not answer. The rest of the profile is unaffected.</span>
          <Btn onClick={loadData}>Try again</Btn>
        </div>
      )}

      {!isLoading && !failed && ratings.length === 0 && (
        <div className="empty">This player has not been rated yet.</div>
      )}

      {!isLoading && !failed && ratings.length > 0 && (
        <>
          <div className="bars">
            {ratings.map((item, index) => (
              <Bar
                key={item.name}
                label={item.name}
                value={item.value}
                max={100}
                display={item.value}
                delay={index * 0.06}
              />
            ))}
          </div>

          <div className="axis" aria-hidden="true">
            <span>0</span>
            <span>50</span>
            <span>100</span>
          </div>

          {best && worst && best.name !== worst.name && (
            <div className="callout">
              Strongest on <b>{best.name}</b> at <b>{best.value}</b>, weakest on{' '}
              <b>{worst.name}</b> at <b>{worst.value}</b> — a {best.value - worst.value} point
              spread across the profile.
            </div>
          )}
        </>
      )}

    </Panel>
  )
}
